import './QuizSummary.css';
import { useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import UnsavedChangesModal from '../UnsavedChangesModal/UnsavedChangesModal';

function QuizSummary() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const skipRef = useRef(false);
    const selectedAnswers = location.state?.selectedAnswers || {};
    const questions = location.state?.questions || [];
    const returnPath = location.state?.returnPath;

    if (!questions || questions.length === 0) {
        return (
            <div className="quiz-summary-page">
                <div className="quiz-summary-container">
                    <div className="not-found">
                        <h1>Kvíz nebyl nalezen</h1>
                        <button onClick={() => navigate(`/kvizy/${id}`)} className="back-button">
                            Zpět na detail kvízu
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const handleQuestionClick = (questionIndex) => {
        skipRef.current = true;
        navigate(`/kvizy/${id}/start`, {
            state: { selectedAnswers, questions, startIndex: questionIndex, returnPath }
        });
    };

    const stripPunctuation = (word) => {
        if (!word) return '';
        return word.replace(/[.,!?;:"""''„"«»()[\]{}]/g, '');
    };

    const getAnswerDisplay = (question) => {
        const answer = selectedAnswers[question.id];

        if (!answer || (Array.isArray(answer) && answer.length === 0)) {
            return <span className="no-answer">Nezodpovězeno</span>;
        }

        switch (question.type) {
            case 'Multichoice':
                const choice = question.choices.find(c => c.id === answer);
                return <span className="answer-text">{choice?.text || 'N/A'}</span>;

            case 'MultipleCorrect':
                const selectedChoices = question.choices
                    .filter(c => answer.includes(c.id))
                    .map(c => c.text);
                return (
                    <span className="answer-text">
                        {selectedChoices.join(', ') || 'Žádná odpověď'}
                    </span>
                );

            case 'FillIn':
                return <span className="answer-text">{answer}</span>;

            case 'Linking':
                const expectedPairCount = question.leftItems.length;
                const actualPairCount = answer?.length || 0;

                if (actualPairCount < expectedPairCount) {
                    return <span className="no-answer">Nebyly propojeny všechny možnosti</span>;
                }

                const pairTexts = answer.map(pair => {
                    const leftItem = question.leftItems.find(item => item.id === pair.left);
                    const rightItem = question.rightItems.find(item => item.id === pair.right);
                    return `(${leftItem?.text || 'N/A'}, ${rightItem?.text || 'N/A'})`;
                });

                return <span className="answer-text">{pairTexts.join(', ')}</span>;

            case 'Timeline':
                const orderedItems = answer.map((itemId, index) => {
                    const item = question.items.find(i => i.id === itemId);
                    return `${index + 1}. ${item?.text || 'N/A'}`;
                });

                return <span className="answer-text">{orderedItems.join(', ')}</span>;

            case 'FindInText':
                if (!Array.isArray(answer) || answer.length === 0) {
                    return <span className="no-answer">Nevybráno</span>;
                }
                const tokens = (question.text || '').split(/\s+/);
                const selectedWordTexts = [...answer].sort((a, b) => a - b)
                    .map(i => tokens[i])
                    .filter(Boolean)
                    .map(w => stripPunctuation(w));
                return <span className="answer-text">{selectedWordTexts.join(', ') || 'N/A'}</span>;

            default:
                return <span className="answer-text">N/A</span>;
        }
    };

    const isAnswered = (question) => {
        const answer = selectedAnswers[question.id];

        // Check if answer exists
        if (answer === undefined || answer === null) {
            return false;
        }

        // For array answers, check if not empty
        if (Array.isArray(answer) && answer.length === 0) {
            return false;
        }

        // For linking questions, check if all pairs are linked
        if (question.type === 'Linking') {
            const expectedPairCount = question.leftItems.length;
            const actualPairCount = answer?.length || 0;
            return actualPairCount >= expectedPairCount;
        }

        return true;
    };

    const answeredCount = questions.filter(q => isAnswered(q)).length;
    const totalQuestions = questions.length;

    return (
        <div className="quiz-summary-page">
            <div className="quiz-summary-container">
                <div className="summary-header">
                    <h1 className="summary-title">Shrnutí kvízu</h1>
                    <div className="summary-stats">
                        <span className="stats-answered">{answeredCount}</span> / {totalQuestions} otázek zodpovězeno
                    </div>
                </div>

                <div className="questions-list">
                    {questions.map((question, index) => {
                        const answered = isAnswered(question);

                        return (
                            <div
                                key={question.id}
                                className={`summary-question-item ${!answered ? 'unanswered' : ''}`}
                                onClick={() => handleQuestionClick(index)}
                            >
                                <div className="question-number">#{index + 1}</div>
                                <div className="question-content">
                                    <div className="question-summary-text">
                                        {question.questionText}
                                    </div>
                                    <div className="question-type-badge">
                                        {question.type === 'Multichoice' && 'Výběr odpovědi'}
                                        {question.type === 'MultipleCorrect' && 'Více správných odpovědí'}
                                        {question.type === 'FillIn' && 'Doplňte odpověď'}
                                        {question.type === 'Linking' && 'Spojování'}
                                        {question.type === 'Timeline' && 'Časová osa'}
                                        {question.type === 'FindInText' && 'Najděte v textu'}
                                    </div>
                                    <div className="question-answer">
                                        <strong>Vaše odpověď:</strong> {getAnswerDisplay(question)}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="summary-actions">
                    <button
                        onClick={() => {
                            skipRef.current = true;
                            navigate(`/kvizy/${id}/start`, { state: { selectedAnswers, questions, returnPath } });
                        }}
                        className="nav-button back-button-summary"
                    >
                        ← Zpět na otázky
                    </button>
                </div>
            </div>

            <div className="summary-sticky-bar">
                <div className="summary-sticky-inner">
                    <span className="summary-sticky-stats">
                        Zodpovězeno: <strong>{answeredCount}</strong> / {totalQuestions}
                    </span>
                    <button
                        onClick={() => {
                            skipRef.current = true;
                            navigate(`/kvizy/${id}/results`, { state: { selectedAnswers, questions, returnPath } });
                        }}
                        className="nav-button review-button"
                    >
                        Vyhodnotit kvíz →
                    </button>
                </div>
            </div>
            <UnsavedChangesModal
                isActive={questions.length > 0}
                skipRef={skipRef}
                message="Kvíz ještě nebyl vyhodnocen. Pokud odejdete, přijdete o svůj postup a všechny odpovědi."
            />
        </div>
    );
}

export default QuizSummary;
